import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { ALL_GAME_TYPES, MULTI_GAME_TYPES, type AdminSettings, type AnyGameType, type GameTimers } from '../../../shared/protocol.js';

type Flash = { ok: boolean; text: string } | null;

const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];

/** Game Arena settings and match history (suunnitelma.md sections 22 and 36). */
export function SettingsAdmin() {
  const [s, setS] = useState<AdminSettings | null>(null);
  const [flash, setFlash] = useState<Flash>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ settings: AdminSettings }>('GET', '/api/admin/settings')
      .then((r) => setS(r.settings))
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));
  }, []);

  if (!s) return flash ? <p class="error">{flash.text}</p> : null;

  const set = <K extends keyof AdminSettings>(key: K, value: AdminSettings[K]) => setS({ ...s, [key]: value });
  const seconds = (value: number, min: number, max: number, onChange: (ms: number) => void, label?: string) => (
    <input
      type="number"
      min={min}
      max={max}
      value={Math.round(value / 1000)}
      onInput={(e) => onChange(Number(e.currentTarget.value) * 1000)}
      aria-label={label}
      required
    />
  );
  const setTimer = (game: AnyGameType, key: keyof GameTimers, ms: number) =>
    set('timers', { ...s.timers, [game]: { ...s.timers[game], [key]: ms } });
  const toggle = (key: 'hitGrantsExtraShot' | 'forbiddenAreaVisible' | 'markSunkSurroundings') => (
    <label class="switch">
      <input type="checkbox" checked={s[key]} onChange={(e) => set(key, e.currentTarget.checked)} />
      <span class="switch-track" aria-hidden="true" />
      {t.admin.settings[key]}
    </label>
  );

  const save = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api<{ settings: AdminSettings }>('PUT', '/api/admin/settings', s);
      setS(r.settings);
      setFlash({ ok: true, text: t.admin.settings.saved });
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const clearHistory = async () => {
    setConfirmClear(false);
    try {
      const r = await api<{ deleted: number }>('POST', '/api/admin/history/clear');
      setFlash({ ok: true, text: t.admin.settings.cleared(r.deleted) });
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  return (
    <>
      <form class="card form" onSubmit={save}>
        <h2>{t.admin.settings.title}</h2>
        <p class="muted">{t.admin.settings.info}</p>
        <h3>{t.admin.settings.timersTitle}</h3>
        <div class="timer-table">
          <span />
          <span class="muted">{t.admin.settings.turnTime}</span>
          <span class="muted">{t.admin.settings.timeBank}</span>
          {/* Snake is played in real time and chess has its clock chosen with the challenge: no turn times here (2026-09-30). */}
          {ALL_GAME_TYPES.filter((g) => g !== 'snake' && g !== 'tetrax' && g !== 'chess').map((g) => (
            <>
              <span class="timer-game">{t.games[g].name}</span>
              {(MULTI_GAME_TYPES as readonly AnyGameType[]).includes(g) ? (
                <span class="muted">{t.admin.settings.turnTimeAtTable}</span>
              ) : (
                seconds(s.timers[g].turnTimeMs, 3, 120, (ms) => setTimer(g, 'turnTimeMs', ms), `${t.games[g].name}: ${t.admin.settings.turnTime}`)
              )}
              {seconds(s.timers[g].timeBankMs, 0, 600, (ms) => setTimer(g, 'timeBankMs', ms), `${t.games[g].name}: ${t.admin.settings.timeBank}`)}
            </>
          ))}
        </div>
        <div class="grid-2">
          <label>
            {t.admin.settings.placementTime}
            {seconds(s.placementTimeMs, 30, 900, (ms) => set('placementTimeMs', ms))}
          </label>
          <label>
            {t.admin.settings.startingPlayer}
            <select
              value={s.startingPlayer}
              onChange={(e) => set('startingPlayer', e.currentTarget.value as AdminSettings['startingPlayer'])}
            >
              {(['random', 'challenger', 'challenged'] as const).map((v) => (
                <option value={v}>{t.admin.settings.starting[v]}</option>
              ))}
            </select>
          </label>
          <label>
            {t.admin.settings.maxActiveGames}
            <input
              type="number"
              min={1}
              max={200}
              value={s.maxActiveGames}
              onInput={(e) => set('maxActiveGames', Number(e.currentTarget.value))}
              required
            />
          </label>
          <label>
            {t.admin.settings.retention}
            <select
              value={s.historyRetentionDays === null ? 'forever' : 'days'}
              onChange={(e) => set('historyRetentionDays', e.currentTarget.value === 'forever' ? null : 365)}
            >
              <option value="forever">{t.admin.settings.retentionForever}</option>
              <option value="days">{t.admin.settings.retentionDays}</option>
            </select>
            {s.historyRetentionDays !== null && (
              <input
                type="number"
                min={1}
                max={3650}
                value={s.historyRetentionDays}
                onInput={(e) => set('historyRetentionDays', Number(e.currentTarget.value))}
                aria-label={t.admin.settings.retentionDays}
                required
              />
            )}
          </label>
        </div>
        <div class="toggles">
          {toggle('hitGrantsExtraShot')}
          {toggle('forbiddenAreaVisible')}
          {toggle('markSunkSurroundings')}
        </div>
        <div class="grid-2">
          <label>
            {t.admin.settings.lobbyChatRetention}
            <input
              type="number"
              min={1}
              max={720}
              value={s.lobbyChatRetentionHours}
              onInput={(e) => set('lobbyChatRetentionHours', Number(e.currentTarget.value))}
              required
            />
          </label>
        </div>
        {flash && (
          <p class={flash.ok ? 'success' : 'error'} role="status">
            {flash.text}
          </p>
        )}
        <button class="btn btn-primary" type="submit" disabled={busy}>
          {t.admin.settings.save}
        </button>
      </form>

      <section class="card">
        <h2>{t.admin.settings.historyTitle}</h2>
        <p class="muted">{t.admin.settings.historyInfo}</p>
        <button class="btn btn-danger" onClick={() => setConfirmClear(true)}>
          {t.admin.settings.clearHistory}
        </button>
      </section>

      {confirmClear && (
        <ConfirmDialog
          title={t.admin.settings.clearConfirmTitle}
          confirmLabel={t.admin.settings.clearHistory}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={clearHistory}
          onCancel={() => setConfirmClear(false)}
        >
          <p>{t.admin.settings.clearConfirmText}</p>
        </ConfirmDialog>
      )}
    </>
  );
}
